/**
 * Campaign Data Capture Utility
 *
 * Captures and stores campaign attribution data including:
 * - UTM parameters (utm_source, utm_medium, utm_campaign, utm_term, utm_content)
 * - Ad platform click IDs (gclid, fbclid, msclkid, li_fat_id)
 * - Referrer URL
 * - Landing page
 * - Timestamp of first visit
 *
 * Data is stored in localStorage and persists across sessions until
 * explicitly cleared (typically after sending to analytics).
 */

const CAMPAIGN_DATA_KEY = 'presage_campaign_data';

/**
 * Get URL parameters from current window location
 */
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const paramObj = {};

  for (const [key, value] of params.entries()) {
    paramObj[key] = value;
  }

  return paramObj;
}

/**
 * Capture campaign data from URL and store in localStorage
 * Only captures on first visit (doesn't overwrite existing data)
 */
export function captureCampaignData() {
  // Check if we already have campaign data stored
  const existingData = getCampaignData();
  if (existingData && existingData.timestamp) {
    // Already captured, don't overwrite
    return existingData;
  }

  const params = getUrlParams();
  const campaignData = {
    // UTM parameters
    utm_source: params.utm_source || null,
    utm_medium: params.utm_medium || null,
    utm_campaign: params.utm_campaign || null,
    utm_term: params.utm_term || null,
    utm_content: params.utm_content || null,

    // Ad platform click IDs
    gclid: params.gclid || null, // Google Ads
    fbclid: params.fbclid || null, // Facebook Ads
    msclkid: params.msclkid || null, // Microsoft Ads
    li_fat_id: params.li_fat_id || null, // LinkedIn Ads

    // Additional context
    referrer: document.referrer || null,
    landing_page: window.location.pathname + window.location.search,
    timestamp: new Date().toISOString(),
  };

  // Only store if we have at least one campaign parameter
  const hasCampaignData =
    campaignData.utm_source ||
    campaignData.utm_medium ||
    campaignData.utm_campaign ||
    campaignData.gclid ||
    campaignData.fbclid ||
    campaignData.msclkid ||
    campaignData.li_fat_id;

  if (hasCampaignData) {
    try {
      localStorage.setItem(CAMPAIGN_DATA_KEY, JSON.stringify(campaignData));
    } catch (error) {
      console.error('Failed to store campaign data:', error);
    }
  }

  return campaignData;
}

/**
 * Retrieve stored campaign data from localStorage
 * Returns null if no data exists
 */
export function getCampaignData() {
  try {
    const data = localStorage.getItem(CAMPAIGN_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve campaign data:', error);
    return null;
  }
}

/**
 * Clear campaign data from localStorage
 * Call this after successfully sending data to analytics
 */
export function clearCampaignData() {
  try {
    localStorage.removeItem(CAMPAIGN_DATA_KEY);
  } catch (error) {
    console.error('Failed to clear campaign data:', error);
  }
}

/**
 * Check if campaign data exists
 */
export function hasCampaignData() {
  const data = getCampaignData();
  return data !== null && Object.keys(data).length > 0;
}
